// Session reporter (spec §2.3 / §3.1 client side).
//
// Owns the session_uuid + monotonic beat_sequence and turns ActivityMeter
// snapshots into cumulative "beats" POSTed to the beat endpoint. Because the
// counters are cumulative, a lost beat is recovered by the next flush.

import { ActivityMeter, MeterSnapshot } from './activityMeter'

export const BEAT_ENDPOINT = '/api/v2/learning_sessions/beat'
export const FLUSH_INTERVAL_MS = 60_000
// Resuming after being hidden this long starts a fresh session.
export const SPLIT_AFTER_MS = 30 * 60_000
// The server closes a session that has had no beat for 5 minutes
// (LearningSession::STALE_AFTER via learning_sessions:close_stale) and ignores
// every later beat for it. Flushes skip when nothing accrued, so a paused video
// easily goes that long; past this gap, continue under a new session instead.
export const ROLLOVER_AFTER_MS = 4 * 60_000

export type CompletionState = 'opened' | 'progressed' | 'completed'

export interface BeatPayload {
  session_uuid: string
  lesson_id: number
  beat_sequence: number
  confirmed_active_seconds: number
  estimated_active_seconds: number
  interaction_count: number
  max_progress: number
  completion_state: CompletionState
  client_started_at: string
  final?: boolean
  diag?: MeterSnapshot['diag']
}

export type BeatTransport = (payload: BeatPayload) => Promise<void>
export type BeaconTransport = (payload: BeatPayload) => void

export interface SessionReporterOptions {
  lessonId: number
  meter: ActivityMeter
  transport?: BeatTransport
  beacon?: BeaconTransport
  uuid?: () => string
  wallNow?: () => number
  diagRate?: number
}

export class SessionReporter {
  private readonly meter: ActivityMeter
  private readonly lessonId: number
  private readonly transport: BeatTransport
  private readonly beacon: BeaconTransport
  private readonly uuid: () => string
  private readonly wallNow: () => number
  private readonly diagRate: number

  private sessionUuid = ''
  private beatSequence = 0
  private clientStartedAt = ''
  private completionState: CompletionState = 'opened'
  private lastSentTotal = -1
  private hiddenSince: number | null = null
  private baseline: MeterSnapshot | null = null
  private baselineDiagAt = Number.NEGATIVE_INFINITY
  private diagSampled = false
  // Wall time and raw meter snapshot of the last beat the server accepted.
  private lastSentAt: number | null = null
  private lastSent: MeterSnapshot | null = null

  constructor(opts: SessionReporterOptions) {
    this.meter = opts.meter
    this.lessonId = opts.lessonId
    this.transport = opts.transport ?? defaultBeatTransport()
    this.beacon = opts.beacon ?? defaultBeaconTransport()
    this.uuid = opts.uuid ?? (() => crypto.randomUUID())
    this.wallNow = opts.wallNow ?? (() => Date.now())
    this.diagRate = Math.min(Math.max(opts.diagRate ?? 0, 0), 100)
  }

  start(): void {
    this.completionState = 'opened'
    this.hiddenSince = null
    this.beginSession(this.meter.snapshot(), this.wallNow())
  }

  // Async flush over HTTP. Skips when nothing new was accumulated unless forced.
  async flush(force = false): Promise<void> {
    if (!this.sessionUuid) return

    const { raw, snap } = this.beatSnapshot()
    const total = snap.confirmedActiveSeconds + snap.estimatedActiveSeconds
    if (!force && total === this.lastSentTotal) return

    const payload = this.toPayload(snap)
    this.lastSentTotal = total
    try {
      await this.transport(payload)
      this.lastSentAt = this.wallNow()
      this.lastSent = raw
    } catch {
      // Cumulative counters mean the next flush recovers the loss; force a resend.
      this.lastSentTotal = -1
    }
  }

  // Synchronous best-effort flush for pagehide (never blocks unload).
  flushBeacon(): void {
    if (!this.sessionUuid) return
    this.beacon(this.toPayload(this.beatSnapshot().snap, true))
  }

  async setCompletionState(state: CompletionState): Promise<void> {
    if (state === this.completionState) return
    this.completionState = state
    await this.flush(true)
  }

  async onHidden(): Promise<void> {
    this.hiddenSince = this.wallNow()
    await this.flush()
  }

  onVisible(): void {
    if (this.hiddenSince !== null && this.wallNow() - this.hiddenSince >= SPLIT_AFTER_MS) {
      this.start() // abandoned session -> split
    }
    this.hiddenSince = null
  }

  async stop(): Promise<void> {
    if (!this.sessionUuid) return
    const payload = this.toPayload(this.beatSnapshot().snap, true)
    try {
      await this.transport(payload)
    } catch {
      // pagehide/stale close remains the fallback.
    }
  }

  get currentSessionUuid(): string {
    return this.sessionUuid
  }

  private beginSession(baseline: MeterSnapshot, startedAt: number): void {
    this.sessionUuid = this.uuid()
    this.beatSequence = 0
    this.lastSentTotal = -1
    this.lastSentAt = null
    this.lastSent = null
    this.clientStartedAt = new Date(startedAt).toISOString()
    this.baseline = baseline
    this.baselineDiagAt = baseline.diag[baseline.diag.length - 1]?.[0] ?? Number.NEGATIVE_INFINITY
    this.diagSampled = isDiagSampled(this.sessionUuid, this.diagRate)
  }

  // Snapshot for the next beat, rolling over to a new session first if the
  // server has likely closed the current one. The new session starts from the
  // last accepted beat, so time accrued since then is carried over, not lost.
  private beatSnapshot(): { raw: MeterSnapshot; snap: MeterSnapshot } {
    const raw = this.meter.snapshot()
    if (this.lastSent && this.lastSentAt !== null && this.wallNow() - this.lastSentAt >= ROLLOVER_AFTER_MS) {
      this.beginSession(this.lastSent, this.lastSentAt)
    }
    return { raw, snap: this.relativeTo(raw) }
  }

  private relativeTo(snap: MeterSnapshot): MeterSnapshot {
    const baseline = this.baseline
    if (!baseline) return snap

    return {
      confirmedActiveSeconds: Math.max(0, snap.confirmedActiveSeconds - baseline.confirmedActiveSeconds),
      estimatedActiveSeconds: Math.max(0, snap.estimatedActiveSeconds - baseline.estimatedActiveSeconds),
      interactionCount: Math.max(0, snap.interactionCount - baseline.interactionCount),
      maxProgress: snap.maxProgress,
      diag: snap.diag.filter((interval) => interval[0] > this.baselineDiagAt)
    }
  }

  private toPayload(snap: MeterSnapshot, final = false): BeatPayload {
    this.beatSequence += 1
    return {
      session_uuid: this.sessionUuid,
      lesson_id: this.lessonId,
      beat_sequence: this.beatSequence,
      confirmed_active_seconds: snap.confirmedActiveSeconds,
      estimated_active_seconds: snap.estimatedActiveSeconds,
      interaction_count: snap.interactionCount,
      max_progress: snap.maxProgress,
      completion_state: this.completionState,
      client_started_at: this.clientStartedAt,
      ...(final ? { final: true } : {}),
      ...(this.diagSampled ? { diag: snap.diag } : {})
    }
  }
}

export function isDiagSampled(sessionUuid: string, rate: number): boolean {
  if (!sessionUuid || rate <= 0) return false
  const hex = sessionUuid.replace(/-/g, '').slice(-2)
  const bucket = Number.parseInt(hex, 16)
  return Number.isFinite(bucket) && bucket < Math.floor(Math.min(rate, 100) * 256 / 100)
}

export function defaultBeatTransport(): BeatTransport {
  return async (payload) => {
    const res = await fetch(BEAT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify(payload)
    })
    if (!res.ok) throw new Error(`beat failed: ${res.status}`)
  }
}

export function defaultBeaconTransport(): BeaconTransport {
  return (payload) => {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    navigator.sendBeacon(BEAT_ENDPOINT, blob)
  }
}

// Wire flush timer, visibility handling, and pagehide beacon. Returns detach fn.
export function bindSessionReporterToDom(reporter: SessionReporter): () => void {
  const timer = window.setInterval(() => {
    void reporter.flush()
  }, FLUSH_INTERVAL_MS)

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      void reporter.onHidden()
    } else {
      reporter.onVisible()
    }
  }
  const onPageHide = () => reporter.flushBeacon()
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) reporter.start()
  }

  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('pagehide', onPageHide)
  window.addEventListener('pageshow', onPageShow)

  return () => {
    window.clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('pagehide', onPageHide)
    window.removeEventListener('pageshow', onPageShow)
  }
}
